import React, { useContext, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { BarChart2, Calendar, Shield, ShieldCheck, Headphones, Lock, RefreshCcw, CircleDollarSign, BanknoteArrowDown, MonitorCheck } from 'lucide-react'

import Contatos from '../LandingPage/Contatos'
import { GlobalContext } from '../../contexts/GlobalContext'
import scrollUtils from '../../util/scroll'
import logo from '../../assets/images/logo2.png'
import video from '../../assets/videos/comercial-prontuario.mp4'


const modulos = [
	{
		id: 1,
		icon: <img src={logo} alt="" className="w-[60px] h-[60px] object-contain" />,
		title: "Prontuário da Saúde do Cidadão",
		description: "Acesse e gerencie seus dados médicos de forma segura e prática.",
		path: '/produtos/prontuario'
	},
	{
		id: 3,
		icon: <BarChart2 size={32} color="#059669" />,
		title: "Enquetes",
		description: "Participe de pesquisas e contribua para melhorias na área da saúde.",
		path: "/produtos/enquetes",
		soon: true
	},
	{
		id: 4,
		icon: <Calendar size={32} color="#059669" />,
		title: "Eventos",
		description: "Acompanhe e agende eventos relacionados à sua saúde.",
		path: "/produtos/eventos",
		soon: true
	}
]

const vantagens = [
	{
		icon: <Shield size={36} color="#059669" />,
		title: "Confiança",
		description: "Soluções testadas e aprovadas por vários tipos de usuário."
	},
	{
		icon: <ShieldCheck size={36} color="#059669" />,
		title: "Segurança de Dados",
		description: "Sua informação é protegida por criptografia, atendendo à LGPD."
	},
	{
		icon: <Headphones size={36} color="#059669" />,
		title: "Suporte Humanizado",
		description: "Equipe especializada pronta para atender suas necessidades em qualquer momento."
	},
	{
		icon: <MonitorCheck size={36} color="#059669" />,
		title: "Acesso imediato",
		description: "Seu acesso é liberado automaticamente, assim que o pagamento for confirmado."
	},
	{
		icon: <BanknoteArrowDown size={36} color="#059669" />,
		title: "7 dias de garantia",
		description: "Caso nossa solução não lhe atenda, você receberá de volta 100% do seu investimento."
	},
	{
		icon: <Lock size={36} color="#059669" />,
		title: "Compra segura",
		description: "Seu pagamento é processado e aprovado de forma totalmente segura."
	},
	{
		icon: <RefreshCcw size={36} color="#059669" />,
		title: "Sempre atualizado",
		description: "Todas as novas atualizações serão disponibilizadas para você automaticamente."
	}
]

const depoimentos = [
	{
		video: "https://integrare-os-minio.nyr4mj.easypanel.host/sigas/guilhermina-rocha.mp4",
		name: "Guilhermina Rocha",
		role: "São Luiz",
		quote: "Facilitou muito meu trabalho com meus pacientes e tem me dado um retorno e uma experiência muito boa."
	},
	{
		image: "https://integrare-os-minio.nyr4mj.easypanel.host/sigas/iramar.jpeg",
		name: "Iramar Benigno",
		role: "Rio de Janeiro",
		quote: "Armado na corrida de hoje de madrugada, mas torcendo para nunca precisar usar."
	},
	{
		image: "https://st4.depositphotos.com/1561509/27622/i/450/depositphotos_276226774-stock-photo-portrait-of-a-middle-aged.jpg",
		name: "Carlos Mendes",
		role: "São Paulo",
		quote: "Consigo carregar meu histórico médico completo na carteira. Isso já me salvou em uma emergência quando viajava."
	},
	{
		image: "https://img.freepik.com/fotos-premium/retrato-de-uma-mulher-morena-de-meia-idade_112793-8696.jpg",
		name: "Patrícia Almeida",
		role: "Belo Horizonte",
		quote: "O Prontuário Medico é excelente para organizar todos os meus exames. Facilita muito."
	}
]

export default function () {
	const { user } = useContext(GlobalContext)

	const history = useHistory()

	useEffect(() => {
		const scrollContainer = document.getElementById('depoimentos-galeria')

		if (window.innerWidth < 1024) {
			return
		}

		function handleScroll(e) {
			e.preventDefault()

			scrollContainer.scrollLeft += e.deltaY
		}

		scrollContainer.addEventListener('wheel', handleScroll, { passive: false })

		return () => {
			scrollContainer.removeEventListener('wheel', handleScroll)
		}
	}, [])

	useEffect(() => {
		scrollUtils.toTop()
	}, [])

	return (
		<div className="min-h-screen bg-background text-foreground flex flex-col">
			<main className="flex-1">
				{/* Hero */}
				<section className="relative bg-gradient-to-r from-slate-900 to-slate-700 py-16 md:py-32 overflow-hidden">
					<div className="container mx-auto px-4 relative z-2 flex flex-col-reverse md:flex-row items-center">
						<div className="md:w-1/2 text-white mb-10 md:mb-0">
							<h1 className="text-3xl md:text-5xl font-bold mb-6 animated fadeIn">Entre no digital com a Positronic</h1>
							<p className="text-[1rem] md:text-[1.1rem] mb-8 leading-relaxed animated fadeIn delay-200ms">
								Nossa solução foi desenvolvida para atender suas necessidades mais específicas. Uma experiência personalizada para o seu perfil.
							</p>
							<a href="#vantagens" className="font-medium bg-white text-slate-800 px-4 py-3 rounded-md hover:text-slate-800 hover:opacity-80 transition-opacity animated fadeIn delay-400ms">
								Saiba mais
							</a>
						</div>

						<div className="md:w-1/2 flex justify-center animated fadeIn delay-300ms slower mb-8 md:mb-0">
							<video 
								src={video} 
								controls 
								autoPlay 
								loop 
								className="max-w-full h-full max-h-[500px] rounded-md" 
							/>
						</div>
					</div>
				</section>

				{/* Vantagens */}
				<section className="py-16 bg-gray-50 bg-slate-200" id="vantagens">
					<div className="container mx-auto px-4">
						<h2 className="text-3xl font-bold text-center mb-12 text-slate-800">Por que escolher nossos serviços</h2>

						<div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">
							{vantagens.map((vantagem, index) => (
								<FeatureCard
									key={index}
									icon={vantagem.icon}
									title={vantagem.title}
									description={vantagem.description}
								/>
							))}
						</div>
					</div>
				</section>

				{/* Módulos */}
				<section className="pb-16 pt-28 px-4 bg-slate-100" id="modulos">
					<div className="container mx-auto">
						<div className="text-center mb-12">
							<h2 className="text-3xl font-bold text-gray-900 mb-4">
								Escolha um Módulo
							</h2>
							<p className="text-lg text-gray-700 max-w-2xl mx-auto">
								Cada módulo pode ser contratado separadamente de acordo com sua necessidade.
							</p>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
							{modulos.map(modulo => (
								<ModuloCard
									key={modulo.title}
									icon={modulo.icon}
									title={modulo.title}
									description={modulo.description}
									onClick={() => {
										history.push({ 
											pathname: !!user ? `/auth${modulo.path}` : modulo.path,
											state: { 
												modulo_id: modulo.id 
											}
										})
									}}
									soon={modulo.soon}
								/>
							))}
						</div>
					</div>
				</section>

				{/* Depoimentos */}
				<section className="py-16 bg-gray-50 bg-slate-200" id="depoimentos">
					<div className="container mx-auto px-4">
						<h2 className="text-3xl font-bold text-center mb-12 text-slate-800">O que nossos clientes dizem</h2>

						<div
							className="max-w-5xl mx-auto flex flex-col gap-4 lg:flex-row lg:width-[90%] overflow-x-auto pb-4 scroll-smooth"
							id="depoimentos-galeria"
						>
							{depoimentos.map((depoimento, index) => (
								<div key={index} className="md:basis-1/2 pl-4 pr-4">
									<Testimonial
										video={depoimento.video}
										image={depoimento.image}
										name={depoimento.name}
										role={depoimento.role}
										quote={depoimento.quote}
									/>
								</div>
							))}
						</div>
					</div>
				</section>

				<Contatos />
			</main>
		</div>
	)
}

function FeatureCard({ icon, title, description }) {
	return (
		<div className="bg-white rounded-xl p-6">
			<div className="w-14 h-14 flex items-center justify-center mb-4">
				{icon}
			</div>
			<h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
			<p className="text-gray-600">{description}</p>
		</div>
	)
}

function ModuloCard({ icon, title, description, onClick, soon }) {
	return (
		<div
			className={`
				hover:shadow-lg transition-shadow duration-300 cursor-pointer border-2 hover:border-primary	
				${soon ? 'opacity-70 cursor-not-allowed' : ''}
			`}
			onClick={onClick}
		>
			<div className="flex flex-col items-center text-center p-6 bg-white h-full">
				<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary mb-4">
					{/* <Icon size={32} color="#059669" /> */}
					{icon}
				</div>

				<h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>

				{soon && (
					<p className="italic bg-yellow-500 px-2 py-1 rounded-md">Em breve</p>
				)}
				<p className="text-gray-600">{description}</p>

				{!soon && (
					<span className="px-4 py-2 rounded-md bg-emerald-500 w-fit hover:opacity-80 mt-4">
						Saiba mais
					</span>
				)}
			</div>
		</div>
	);
}

function Testimonial({ image, video, name, role, quote }) {
	if(video) {
		return (
			<div className="bg-white rounded-xl shadow-suave p-6 h-full flex flex-col lg:w-[30rem]">
				<div className="mb-4 grid grid-cols-[9rem_auto] gap-1 items-center">
					<video src={video} alt={name} controls className="h-36 w-36 border-2 object-cover rounded-md" />

					<div className="flex flex-col gap-3">
						<div className="ml-4">
							<h4 className="font-semibold text-gray-900">{name}</h4>
							<p className="text-sm text-gray-600">{role}</p>
						</div>

						<blockquote className="text-gray-700 italic flex-1 text-sm sm:text-base">
							"{quote}"
						</blockquote>
					</div>
				</div>
				
			</div>
		)
	}

	return (
		<div className="bg-white rounded-xl shadow-suave p-6 h-full flex flex-col lg:w-96">
			<div className="mb-4 flex items-center">
				<img src={image} alt={name} className="h-12 w-12 border-2 object-cover rounded-full" />

				<div className="ml-4">
					<h4 className="font-semibold text-gray-900">{name}</h4>
					<p className="text-sm text-gray-600">{role}</p>
				</div>
			</div>
			<blockquote className="text-gray-700 italic flex-1 text-sm sm:text-base">
				"{quote}"
			</blockquote>
		</div>
	)
}